import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsService } from '@/services/analytics.service';
import {
  InstanceAnalytics,
  PostAnalytics,
  CampaignAnalytics,
  RecordAnalyticsData,
  AnalyticsData,
  AnalyticsHubFilters,
  AnalyticsHubResponse,
  AnalyticsHubDetail,
} from '@/types/analytics';
import { useAuth } from '@/hooks/useAuth';
import { teamQueryKeys, getEffectiveTeamId } from '@/lib/queryKeys';

/**
 * React Query hooks for analytics
 */

/**
 * Fetch analytics for a scheduled instance (all channels)
 */
export const useInstanceAnalytics = (instanceId: number) => {
  return useQuery<InstanceAnalytics, Error>({
    queryKey: ['analytics', 'instance', instanceId],
    queryFn: () => analyticsService.getInstanceAnalytics(instanceId),
    enabled: !!instanceId && instanceId > 0,
    staleTime: 60000, // 1 minute - analytics don't change frequently
  });
};

/**
 * Fetch aggregated analytics for a post (all publishes)
 */
export const usePostAnalytics = (postId: number) => {
  return useQuery<PostAnalytics, Error>({
    queryKey: ['analytics', 'post', postId],
    queryFn: () => analyticsService.getPostAnalytics(postId),
    enabled: !!postId && postId > 0,
    staleTime: 60000, // 1 minute
  });
};

/**
 * Fetch aggregated analytics for a variation
 */
export const useVariationAnalytics = (variationId: number) => {
  return useQuery<AnalyticsData, Error>({
    queryKey: ['analytics', 'variation', variationId],
    queryFn: () => analyticsService.getVariationAnalytics(variationId),
    enabled: !!variationId && variationId > 0,
    staleTime: 60000, // 1 minute
  });
};

/**
 * Fetch campaign analytics (all posts in campaign)
 */
export const useCampaignAnalytics = (campaignId: number) => {
  return useQuery<CampaignAnalytics, Error>({
    queryKey: ['analytics', 'campaign', campaignId],
    queryFn: () => analyticsService.getCampaignAnalytics(campaignId),
    enabled: !!campaignId && campaignId > 0,
    staleTime: 60000, // 1 minute
  });
};

/**
 * Record analytics manually (internal/webhook use)
 */
export const useRecordAnalytics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RecordAnalyticsData) => analyticsService.recordAnalytics(data),
    onSuccess: () => {
      // Invalidate related analytics queries
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      // Specific invalidations based on the channel instance
      queryClient.invalidateQueries({ queryKey: ['analytics', 'instance'] });
    },
  });
};

/**
 * Fetch Analytics Hub data with filters
 */
export const useAnalyticsHub = (filters: AnalyticsHubFilters) => {
  const { user } = useAuth();
  const teamId = getEffectiveTeamId(user);

  return useQuery<AnalyticsHubResponse, Error>({
    queryKey: teamQueryKeys.analytics.hub(teamId, filters as unknown as Record<string, unknown>),
    queryFn: () => analyticsService.getHubData(filters),
    enabled: !!teamId && !!filters.from && !!filters.to,
    staleTime: 60000,
    placeholderData: (previousData) => previousData, // keepPreviousData equivalent in TanStack Query v5
  });
};

/**
 * Fetch Analytics Hub detail for a specific channel instance
 */
export const useAnalyticsHubDetail = (channelInstanceId: number | null) => {
  const { user } = useAuth();
  const teamId = getEffectiveTeamId(user);

  return useQuery<AnalyticsHubDetail, Error>({
    queryKey: teamQueryKeys.analytics.hubDetail(teamId, channelInstanceId ?? 0),
    queryFn: () => analyticsService.getHubDetail(channelInstanceId!),
    enabled: !!teamId && !!channelInstanceId && channelInstanceId > 0,
    staleTime: 60000,
  });
};

